// src/Components/FamilyDirectory.js
import React, { useState, useEffect } from 'react';
import { sanitizeHref } from '../utils/urlSanitizer';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LocationOn as LocationIcon,
  Badge as CertIcon,
  School as SchoolIcon,
  Star as StarIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Build as ServiceIcon,
  Close as CloseIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { useProfile } from '../contexts/ProfileContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import logger from '../utils/logger';

const FamilyDirectory = () => {
  const { profile } = useProfile();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'card'
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewTab, setViewTab] = useState(0);

  // Fetch family members with professional profiles from nested members collection
  useEffect(() => {
    const fetchMembers = async () => {
      if (!profile?.familyCode) {
        setLoading(false);
        return;
      }

      try {
        // Query from nested members collection where professionalProfile data is stored
        const membersRef = collection(db, 'families', profile.familyCode, 'members');
        const snapshot = await getDocs(membersRef);

        const membersList = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(member => {
            // Show members who have opted in to directory OR have professional info
            const hasBusinessInfo = member.businessInfo?.showInDirectory !== false;
            const hasProfessionalProfile = member.professionalProfile?.profession ||
                                          member.professionalProfile?.skills?.length > 0 ||
                                          member.professionalProfile?.services?.length > 0;
            return hasBusinessInfo || hasProfessionalProfile;
          });

        setMembers(membersList);
        setFilteredMembers(membersList);
        setError(null);
      } catch (err) {
        logger.error('Error fetching family members:', err);
        setError('Failed to load family directory');
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [profile?.familyCode]);

  // Filter members based on search and filters
  useEffect(() => {
    let filtered = [...members];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member => {
        const name = `${member.firstName} ${member.lastName}`.toLowerCase();
        const profession = member.professionalProfile?.profession?.toLowerCase() || '';
        const company = member.professionalProfile?.company?.toLowerCase() || '';
        const businessName = member.businessInfo?.businessName?.toLowerCase() || '';
        const skills = member.professionalProfile?.skills?.join(' ').toLowerCase() || '';
        const services = member.professionalProfile?.services?.map(s => s.name).join(' ').toLowerCase() || '';

        return name.includes(term) ||
               profession.includes(term) ||
               company.includes(term) ||
               businessName.includes(term) ||
               skills.includes(term) ||
               services.includes(term);
      });
    }

    // Industry filter
    if (selectedIndustry) {
      filtered = filtered.filter(member =>
        member.professionalProfile?.industry === selectedIndustry
      );
    }

    // Service filter
    if (selectedService) {
      filtered = filtered.filter(member =>
        member.professionalProfile?.services?.some(s => s.category === selectedService)
      );
    }

    // Available only filter
    if (availableOnly) {
      filtered = filtered.filter(member =>
        member.professionalProfile?.isAvailableForServices === true
      );
    }

    setFilteredMembers(filtered);
  }, [members, searchTerm, selectedIndustry, selectedService, availableOnly]);

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setDetailsOpen(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedIndustry('');
    setSelectedService('');
    setAvailableOnly(false);
  };

  const getAvailableIndustries = () => {
    const industries = new Set();
    members.forEach(member => {
      if (member.professionalProfile?.industry) {
        industries.add(member.professionalProfile.industry);
      }
    });
    return Array.from(industries).sort();
  };

  const getAvailableServices = () => {
    const services = new Set();
    members.forEach(member => {
      member.professionalProfile?.services?.forEach(service => {
        if (service.category) {
          services.add(service.category);
        }
      });
    });
    return Array.from(services).sort();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Alert severity="info">
        Join a family to view the Family Business Directory.
      </Alert>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, profession, skill, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Industry</InputLabel>
              <Select
                value={selectedIndustry}
                label="Industry"
                onChange={(e) => setSelectedIndustry(e.target.value)}
              >
                <MenuItem value="">All Industries</MenuItem>
                {getAvailableIndustries().map(industry => (
                  <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Service Type</InputLabel>
              <Select
                value={selectedService}
                label="Service Type"
                onChange={(e) => setSelectedService(e.target.value)}
              >
                <MenuItem value="">All Services</MenuItem>
                {getAvailableServices().map(service => (
                  <MenuItem key={service} value={service}>{service}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant={availableOnly ? 'contained' : 'outlined'}
              onClick={() => setAvailableOnly(!availableOnly)}
              startIcon={<StarIcon />}
              size="small"
            >
              Available
            </Button>
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant="text"
              onClick={clearFilters}
              size="small"
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Count + View Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredMembers.length} of {members.length} family members
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="list"><Tooltip title="List view"><ViewListIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="card"><Tooltip title="Card view"><GridViewIcon fontSize="small" /></Tooltip></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Member List / Cards */}
      {filteredMembers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No professionals found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or clear filters
          </Typography>
        </Paper>
      ) : viewMode === 'list' ? (
        /* ── Compact list view ── */
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <List disablePadding>
            {filteredMembers.map((member, idx) => (
              <React.Fragment key={member.id}>
                <ListItemButton
                  onClick={() => handleViewDetails(member)}
                  sx={{ py: 1, px: 2 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={member.photoURL}
                      sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.85rem' }}
                    >
                      {member.firstName?.[0]}{member.lastName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {member.firstName} {member.lastName}
                        </Typography>
                        {member.professionalProfile?.isAvailableForServices && (
                          <Chip label="Available" color="success" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      [
                        member.professionalProfile?.profession,
                        member.businessInfo?.businessName,
                        member.professionalProfile?.industry,
                      ].filter(Boolean).join(' · ') || null
                    }
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItemButton>
                {idx < filteredMembers.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }} />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        /* ── Card grid view ── */
        <Grid container spacing={2}>
          {filteredMembers.map((member) => (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar src={member.photoURL} sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                      {member.firstName?.[0]}{member.lastName?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {member.firstName} {member.lastName}
                      </Typography>
                      {member.professionalProfile?.profession && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {member.professionalProfile.profession}
                          {member.businessInfo?.businessName ? ` · ${member.businessInfo.businessName}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {member.professionalProfile?.industry && (
                    <Chip label={member.professionalProfile.industry} size="small" variant="outlined" sx={{ mb: 1 }} />
                  )}

                  {member.professionalProfile?.skills?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {member.professionalProfile.skills.slice(0, 3).map((skill, idx) => (
                        <Chip key={idx} label={skill} size="small" color="primary" variant="outlined" />
                      ))}
                      {member.professionalProfile.skills.length > 3 && (
                        <Chip label={`+${member.professionalProfile.skills.length - 3}`} size="small" />
                      )}
                    </Box>
                  )}

                  {member.professionalProfile?.isAvailableForServices && (
                    <Chip icon={<StarIcon />} label="Available for hire" color="success" size="small" sx={{ mt: 0.5 }} />
                  )}
                </CardContent>

                <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleViewDetails(member)}
                  >
                    View Profile
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Member Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedMember && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={selectedMember.photoURL}
                  sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}
                >
                  {selectedMember.firstName?.[0]}{selectedMember.lastName?.[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </Typography>
                  {selectedMember.professionalProfile?.profession && (
                    <Typography variant="subtitle1" color="text.secondary">
                      {selectedMember.professionalProfile.profession}
                      {selectedMember.professionalProfile?.company && ` at ${selectedMember.professionalProfile.company}`}
                    </Typography>
                  )}
                </Box>
                <IconButton onClick={() => setDetailsOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Tabs
                value={viewTab}
                onChange={(e, v) => setViewTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab icon={<PersonIcon />} iconPosition="start" label="Overview" />
                <Tab icon={<ServiceIcon />} iconPosition="start" label="Services" />
                <Tab icon={<BusinessIcon />} iconPosition="start" label="Business" />
              </Tabs>

              {/* Overview Tab */}
              {viewTab === 0 && (
                <Box>
                  {/* Professional Info */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>Professional Info</Typography>
                      <List dense>
                        {selectedMember.professionalProfile?.jobTitle && (
                          <ListItem>
                            <ListItemIcon><WorkIcon /></ListItemIcon>
                            <ListItemText
                              primary="Job Title"
                              secondary={selectedMember.professionalProfile.jobTitle}
                            />
                          </ListItem>
                        )}
                        {selectedMember.professionalProfile?.industry && (
                          <ListItem>
                            <ListItemIcon><BusinessIcon /></ListItemIcon>
                            <ListItemText
                              primary="Industry"
                              secondary={selectedMember.professionalProfile.industry}
                            />
                          </ListItem>
                        )}
                        {selectedMember.professionalProfile?.yearsOfExperience > 0 && (
                          <ListItem>
                            <ListItemIcon><TimeIcon /></ListItemIcon>
                            <ListItemText
                              primary="Experience"
                              secondary={`${selectedMember.professionalProfile.yearsOfExperience} years`}
                            />
                          </ListItem>
                        )}
                        {selectedMember.professionalProfile?.serviceArea && (
                          <ListItem>
                            <ListItemIcon><LocationIcon /></ListItemIcon>
                            <ListItemText
                              primary="Service Area"
                              secondary={selectedMember.professionalProfile.serviceArea}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {/* Availability Info */}
                      {selectedMember.professionalProfile?.isAvailableForServices && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" gutterBottom>Availability</Typography>
                          <Alert severity="success" icon={<StarIcon />}>
                            Available for services
                          </Alert>
                          <List dense>
                            {selectedMember.professionalProfile?.hourlyRate && (
                              <ListItem>
                                <ListItemIcon><MoneyIcon /></ListItemIcon>
                                <ListItemText
                                  primary="Rate"
                                  secondary={selectedMember.professionalProfile.hourlyRate}
                                />
                              </ListItem>
                            )}
                            {selectedMember.professionalProfile?.availability && (
                              <ListItem>
                                <ListItemIcon><TimeIcon /></ListItemIcon>
                                <ListItemText
                                  primary="Availability"
                                  secondary={selectedMember.professionalProfile.availability}
                                />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {/* Skills */}
                  {selectedMember.professionalProfile?.skills?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>Skills & Expertise</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedMember.professionalProfile.skills.map((skill, idx) => (
                          <Chip key={idx} label={skill} color="primary" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Certifications */}
                  {selectedMember.professionalProfile?.certifications?.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        <CertIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        Certifications
                      </Typography>
                      <Grid container spacing={1}>
                        {selectedMember.professionalProfile.certifications.map((cert, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Paper sx={{ p: 1.5 }} variant="outlined">
                              <Typography variant="subtitle2">{cert.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cert.issuer}{cert.year && ` (${cert.year})`}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Education */}
                  {selectedMember.professionalProfile?.education?.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        <SchoolIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        Education
                      </Typography>
                      {selectedMember.professionalProfile.education.map((edu, idx) => (
                        <Paper key={idx} sx={{ p: 1.5, mb: 1 }} variant="outlined">
                          <Typography variant="subtitle2">
                            {edu.degree ? `${edu.degree} in ${edu.field}` : edu.field || edu.institution}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {edu.institution}{edu.year && ` (${edu.year})`}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {/* Work History */}
                  {selectedMember.professionalProfile?.workHistory?.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        <WorkIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        Work History
                      </Typography>
                      {selectedMember.professionalProfile.workHistory.map((work, idx) => (
                        <Paper key={idx} sx={{ p: 1.5, mb: 1 }} variant="outlined">
                          <Typography variant="subtitle2">{work.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {work.company}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {work.startYear} - {work.current ? 'Present' : work.endYear}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Services Tab */}
              {viewTab === 1 && (
                <Box>
                  {selectedMember.professionalProfile?.services?.length > 0 ? (
                    <Grid container spacing={2}>
                      {selectedMember.professionalProfile.services.map((service, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="h6">{service.name}</Typography>
                              {service.category && (
                                <Chip
                                  label={service.category}
                                  size="small"
                                  sx={{ mt: 0.5, mb: 1 }}
                                />
                              )}
                              {service.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {service.description}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      No services listed yet.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Business Tab */}
              {viewTab === 2 && (
                <Box>
                  {selectedMember.businessInfo?.businessName ? (
                    <>
                      <Typography variant="h6" gutterBottom>
                        {selectedMember.businessInfo.businessName}
                      </Typography>

                      {selectedMember.businessInfo?.businessDescription && (
                        <Typography variant="body1" paragraph>
                          {selectedMember.businessInfo.businessDescription}
                        </Typography>
                      )}

                      <Grid container spacing={2}>
                        {selectedMember.businessInfo?.businessPhone && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon color="action" />
                              <Link href={sanitizeHref(`tel:${selectedMember.businessInfo.businessPhone}`)}>
                                {selectedMember.businessInfo.businessPhone}
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.businessEmail && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon color="action" />
                              <Link href={sanitizeHref(`mailto:${selectedMember.businessInfo.businessEmail}`)}>
                                {selectedMember.businessInfo.businessEmail}
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.website && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WebsiteIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.website)} target="_blank" rel="noopener noreferrer">
                                Website
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.linkedIn && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkedInIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.linkedIn)} target="_blank" rel="noopener noreferrer">
                                LinkedIn Profile
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.twitter && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WorkIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.twitter.startsWith('http') ? selectedMember.businessInfo.twitter : `https://x.com/${selectedMember.businessInfo.twitter.replace('@', '')}`)} target="_blank" rel="noopener noreferrer">
                                Twitter / X
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.instagram && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InstagramIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.instagram.startsWith('http') ? selectedMember.businessInfo.instagram : `https://instagram.com/${selectedMember.businessInfo.instagram.replace('@', '')}`)} target="_blank" rel="noopener noreferrer">
                                Instagram
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.facebook && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FacebookIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.facebook.startsWith('http') ? selectedMember.businessInfo.facebook : `https://facebook.com/${selectedMember.businessInfo.facebook}`)} target="_blank" rel="noopener noreferrer">
                                Facebook
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {selectedMember.businessInfo?.portfolioUrl && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WorkIcon color="action" />
                              <Link href={sanitizeHref(selectedMember.businessInfo.portfolioUrl)} target="_blank" rel="noopener noreferrer">
                                Portfolio
                              </Link>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  ) : (
                    <Alert severity="info">
                      No business information provided.
                    </Alert>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {selectedMember.businessInfo?.businessEmail && (
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  href={sanitizeHref(`mailto:${selectedMember.businessInfo.businessEmail}`)}
                >
                  Contact
                </Button>
              )}
              <Button onClick={() => setDetailsOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default FamilyDirectory;
